import { AppDispatch } from "@/app/store";
import { useDispatch, useSelector } from "react-redux";
import TableList, { TableColumn } from "@/components/common/TableList";
import { selectPaymentItems } from "@/features/payments/paymentsSelectors";
import { fetchPaymentItems, fetchUpdatePurchaseConcept, fetchAddPurchaseConcept, fetchDeletePurchaseConcept } from "@/features/options/optionsThunks";
import { PaymentConcept } from "@/types";
import { Alert, Button, IconButton, Snackbar, Switch, Tooltip, Typography, TextField, Stack, FormControl, InputAdornment } from "@mui/material";
import { Box } from "@mui/system";
import { FC, useEffect, useState, useCallback } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoadingOverlay from "@/components/common/LoadingOverlay";
import { Upload } from "@mui/icons-material";
import { ItemsModal, CustomAction } from "@/components/common/ItemsModal";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const PurchaseItemTable: FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const paymentItems = useSelector(selectPaymentItems);

    const [isDeleting, setIsDeleting] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<PaymentConcept | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);
    const [itemName, setItemName] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [itemAmount, setItemAmount] = useState('');
    const [itemQuantity, setItemQuantity] = useState('1');
    const [itemActive, setItemActive] = useState(true);

    useEffect(() => {
        if (paymentItems.length === 0) {
            dispatch(fetchPaymentItems());
        }
    }, [dispatch])

    const [snackOpen, setSnackOpen] = useState(false)
    const [snackMsg, setSnackMsg] = useState('')
    const [snackSeverity, setSnackSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('error')
    const showSnackbar = (msg: string, sev: 'success' | 'error' | 'info' | 'warning' = 'error') => {
        setSnackMsg(msg);
        setSnackSeverity(sev);
        setSnackOpen(true)
    }
    const handleSnackClose = () => setSnackOpen(false)

    const handleEdit = async (item: PaymentConcept) => {
        try {
            setIsEditMode(true);
            setEditingItemId(item.id);
            setItemName(item.name);
            setItemDescription(item.description || '');
            setItemAmount(item.amount.toString());
            setItemQuantity(item.cant.toString());
            setItemActive(item.active ?? false);

            setModalOpen(true);
        } catch (err) {
            console.error('Error handling edit:', err)
            showSnackbar('Error al iniciar edición. Por favor, intente nuevamente.', 'error')
        }
    };

    const handleDelete = useCallback((item: PaymentConcept) => {
        if (!item.id) {
            showSnackbar('Error: ID de concepto no válido', 'error');
            return;
        }
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }, []);

    const cancelDelete = useCallback(() => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!itemToDelete?.id) {
            setDeleteDialogOpen(false);
            return;
        }
        setIsDeleting(true);
        try {
            await dispatch(fetchDeletePurchaseConcept(itemToDelete.id)).unwrap();

            showSnackbar('Concepto de pago eliminado correctamente', 'success');
            await dispatch(fetchPaymentItems());

            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (error: any) {
            showSnackbar(error?.message || 'Error al eliminar el concepto', 'error');
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    }, [dispatch, itemToDelete]);

    const handleToggleActive = async (item: PaymentConcept, checked: boolean) => {
        setIsUpdating(true);
        try {
            const updatePayload: PaymentConcept = {
                id: item.id,
                active: checked,
                name: item.name,
                description: item.description,
                amount: item.amount,
                cant: item.cant
            };
            await dispatch(fetchUpdatePurchaseConcept(updatePayload)).unwrap()
            showSnackbar(
                `${item.name} ${checked ? 'activado' : 'desactivado'} correctamente`,
                'success'
            );
        } catch (err) {
            showSnackbar('Error al actualizar el estado. Por favor, intente de nuevo.', 'error');
        } finally {
            setIsUpdating(false);
        }
    }

    const handleCreate = () => {
        setIsEditMode(false);
        setEditingItemId(null);
        setModalOpen(true);
    };


    const handleCloseModal = () => {
        setModalOpen(false);
        setIsEditMode(false);
        setEditingItemId(null);
        setItemName('');
        setItemDescription('');
        setItemAmount('');
        setItemQuantity('1');
        setItemActive(true);
        setModalError(null);
    };

    const validateForm = (): { valid: boolean; error?: string } => {
        if (!itemName.trim()) {
            return { valid: false, error: 'El nombre es obligatorio' };
        }
        if (!itemDescription.trim()) {
            return { valid: false, error: 'La descripción es obligatoria' };
        }
        if (!itemAmount || parseFloat(itemAmount) <= 0) {
            return { valid: false, error: 'El valor debe ser mayor a 0' };
        }
        if (!itemQuantity || parseInt(itemQuantity) <= 0) {
            return { valid: false, error: 'La cantidad debe ser mayor a 0' };
        }
        return { valid: true };
    };

    
    const handleConfirmSubmit = async () => {
        const validation = validateForm();
        if (!validation.valid) {
            setModalError(validation.error!);
            return;
        }

        setSubmitting(true);
        setModalError(null);

        try {
            if (isEditMode && editingItemId) {
                const updatePayload: PaymentConcept = {
                    id: editingItemId,
                    name: itemName.trim(),
                    description: itemDescription.trim(),
                    amount: parseFloat(itemAmount),
                    cant: parseInt(itemQuantity),
                    active: itemActive
                };

                await dispatch(fetchUpdatePurchaseConcept(updatePayload)).unwrap();
                showSnackbar('Concepto de pago actualizado correctamente', 'success');
            } else {
                // CREAR
                const newItem: PaymentConcept = {
                    id: '',
                    name: itemName.trim(),
                    description: itemDescription.trim(),
                    amount: parseFloat(itemAmount),
                    cant: parseInt(itemQuantity),
                    active: itemActive
                };

                await dispatch(fetchAddPurchaseConcept(newItem)).unwrap();
                showSnackbar('Concepto de pago agregado correctamente', 'success');
            }

            // Refrescar lista
            await dispatch(fetchPaymentItems());
            handleCloseModal();
        } catch (err: any) {
            console.error('Error submitting payment concept:', err);
            setModalError(err?.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el concepto de pago`);
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Acciones personalizadas del modal
     */
    const modalActions: CustomAction[] = [
        {
            label: 'Cancelar',
            onClick: handleCloseModal,
            variant: 'outlined',
            disabled: submitting
        },
        {
            label: isEditMode ? 'Actualizar' : 'Agregar',
            onClick: handleConfirmSubmit,
            variant: 'contained',
            disabled: submitting,
            loading: submitting
        }
    ];
    const columns: TableColumn<PaymentConcept>[] = [
        {
            key: 'name',
            label: 'Título',
            width: 360,
            headerSx: { color: '#374151' }
        },
        {
            key: 'description',
            label: 'Descripción',
            align: 'left',
            headerSx: { color: '#374151' }
        },
        {
            key: 'active',
            label: 'Estado',
            align: 'center',
            render: (pi) => {
                const isDisabled = isUpdating || pi.name === 'Otros';

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title={isDisabled ? 'No se puede cambiar el estado de "otro"' : (pi.active ? 'Desactivar' : 'Activar')}>
                            <span> {/* Span necesario para aplicar Tooltip a un componente deshabilitado */}
                                <Switch
                                    checked={pi.active}
                                    onChange={(e) => handleToggleActive(pi, e.target.checked)}
                                    disabled={isDisabled}
                                    size="small"
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#00695c',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#00695c',
                                        },
                                        opacity: isDisabled ? 0.5 : 1
                                    }}
                                />
                            </span>
                        </Tooltip>
                        <Typography
                            variant="caption"
                            sx={{
                                color: pi.active ? '#00695c' : '#9ca3af',
                                fontWeight: 500,
                                minWidth: 55,
                                opacity: isDisabled ? 0.5 : 1
                            }}
                        >
                            {pi.active ? 'Activo' : 'Inactivo'}
                        </Typography>
                    </Box>
                )
            },
            headerSx: { color: '#374151' }
        },
        {
            key: 'amount',
            label: '$ Valor',
            headerSx: { color: '#374151' },
            render: (pi) => `$${pi.amount.toLocaleString('es-CO')}`
        },
        {
            key: 'actions',
            label: 'Acciones',
            align: 'center',
            width: 120,
            render: (i) => {
                const isOtroConcept = i.name === 'Otros';
                const disabledColor = '#d1d5db';

                return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title={isOtroConcept ? 'No se puede editar el concepto "Otros"' : 'Editar'}>
                            <IconButton
                                size="small"
                                onClick={() => handleEdit(i)}
                                disabled={isOtroConcept}
                                sx={{
                                    color: isOtroConcept ? disabledColor : '#6b7280',
                                    '&:hover': { color: isOtroConcept ? disabledColor : 'primary.main' }
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isOtroConcept ? 'No se puede eliminar el concepto "Otros"' : 'Eliminar'}>
                            <IconButton
                                size="small"
                                onClick={() => handleDelete(i)}
                                disabled={isDeleting || isOtroConcept}
                                sx={{
                                    color: isDeleting || isOtroConcept ? disabledColor : '#6b7280',
                                    '&:hover': { color: isDeleting || isOtroConcept ? disabledColor : 'error.main' }
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            },
            cellSx: { px: 1 }
        }
    ];

    const footer = (
        <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={handleCreate}
            sx={{
                textTransform: 'none',
                px: 3,
                backgroundColor: 'primary.dark',
                color: 'white',
                '&:hover': { backgroundColor: '#111827' }
            }}
        >
            Agregar nuevo Item
        </Button>
    );

    return (
        <>
            {(isDeleting || isUpdating) && (
                <LoadingOverlay
                    message={isDeleting ? "Eliminando documento..." : "Actualizando estado..."}
                />
            )}

            <Snackbar
                open={snackOpen}
                autoHideDuration={3000}
                onClose={handleSnackClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackClose}
                    severity={snackSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackMsg}
                </Alert>
            </Snackbar>

            <ConfirmDialog
                open={deleteDialogOpen}
                title="Confirmar eliminación"
                message="¿Estás seguro de que quieres eliminar el concepto de pago"
                itemName={itemToDelete?.name}
                warningMessage="Esta acción no se puede deshacer. El concepto será eliminado permanentemente."
                confirmText="Eliminar"
                cancelText="Cancelar"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
                confirmColor="error"
                isLoading={isDeleting}
            />

            <Box sx={{ py: 6, px: { xs: 4, md: 6, lg: 8 } }}>
                <TableList
                    data={paymentItems}
                    columns={columns}
                    title="Items de Pago"
                    getRowKey={(pi) => pi.id || ''}
                    containerSx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}
                    tableSx={{}}
                    footer={footer}
                    headerBackground={true}
                    emptyMessage="No hay items de pago registrados"
                />
            </Box>

            <ItemsModal
                open={modalOpen}
                onClose={handleCloseModal}
                title={isEditMode ? 'Editar Concepto de Pago' : 'Agregar Nuevo Concepto de Pago'}
                showCustomActions={true}
                customActions={modalActions}
                isLoading={submitting}
                modalStyles={{ width: { xs: '90%', sm: '60%', md: '50%', lg: '60%' } }}
            >
                <Stack spacing={3} sx={{
                    mt: 1,
                    px: { lg: 6, md: 3 },
                    width: "80%",
                    display: "block",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}>
                    {modalError && <Alert severity="error">{modalError}</Alert>}

                    {/* Nombre del Item */}
                    <FormControl fullWidth>
                        <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                            Nombre del Concepto *
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Ej: Matrícula, Pensión, Uniforme"
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                            disabled={submitting}
                            variant="outlined"
                        />
                    </FormControl>

                    {/* Descripción */}
                    <FormControl fullWidth>
                        <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                            Descripción *
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Descripción del concepto de pago"
                            value={itemDescription}
                            onChange={(e) => setItemDescription(e.target.value)}
                            disabled={submitting}
                            variant="outlined"
                        />
                    </FormControl>

                    {/* Valor y Cantidad en fila */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        {/* Valor */}
                        <FormControl fullWidth>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                                Valor *
                            </Typography>
                            <TextField
                                fullWidth
                                type="number"
                                placeholder="0"
                                value={itemAmount}
                                onChange={(e) => setItemAmount(e.target.value)}
                                disabled={submitting}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                inputProps={{
                                    min: 0,
                                    step: 1000
                                }}
                            />
                        </FormControl>

                    </Box>
                    {!isEditMode && (
                        <Alert severity="info">
                            El concepto se creará en estado <strong>Activo</strong> por defecto
                        </Alert>
                    )}
                </Stack>
            </ItemsModal>
        </>
    );
};

export default PurchaseItemTable